import type { ReactNode } from 'react';

import { useIntl } from 'react-intl';
import { useStyles } from 'uinix-ui';

import { ProductMenuApp } from '~/features/app/types';

import { Box } from '../../components/Box/Box';
import { ContentContainer } from '../../components/ContentContainer/ContentContainer';
import { Layout } from '../../components/Layout/Layout';
import { Link } from '../../components/Link/Link';
import { Logo } from '../../components/Logo/Logo';
import { StatusMessage } from '../../components/StatusMessage/StatusMessage';
import { DEPRECATED_REBRAND_PAGE_HEADER_TESTID } from '../../constants/id';
import { Action } from '../../types/Action';
import { NavLink } from '../../types/NavLink';
import { Nullable } from '../../types/Nullable';
import { Option } from '../../types/Option';
import { StatusType } from '../../types/StatusType';
import { Theme } from '../../types/Theme';
import { User } from '../../types/User';
import { getUserInitials } from '../../utils/getUserInitials';
import { getUserName } from '../../utils/getUserName';
import { MainNav } from './MainNav';
import { NavPill } from './NavPill';
import { ProductMenu } from './ProductMenu';
import { SubNav } from './SubNav';
import { testIsActiveLink } from './utils';

interface Props {
  activePathname: string;
  help: {
    actions: Action[];
  };
  links: NavLink[];
  profile: {
    settings: Option[];
    user: User;
  };
  onSearchClick: () => void;
  apps?: ProductMenuApp[];
  banner?: Nullable<{
    message: string;
    status: StatusType;
    onDismiss?: () => void;
  }>;
  ctaButton?: ReactNode;
  isLoading?: boolean;
  preSearchAction?: ReactNode;
  theme?: Theme;
  onHelpClick?: () => void;
}

export const PageHeader = ({
  activePathname,
  apps,
  banner,
  ctaButton,
  help,
  isLoading,
  links,
  onHelpClick,
  onSearchClick,
  preSearchAction,
  profile,
  theme = 'light',
}: Props) => {
  const styles = useStyles();
  const intl = useIntl();

  const sublinks = links.find(testIsActiveLink(activePathname))
    ?.children as NavLink[];

  const isDarkTheme = theme === 'dark';

  const searchTooltip = intl.formatMessage(
    {
      id: 'PageHeader.searchTooltip',
      defaultMessage: 'Search {shortcut}',
      description:
        'Search tooltip with keyboard shortcut. Example: shortcut = "⌘ + J" or "Ctrl + J"',
    },
    {
      shortcut:
        navigator.platform.toUpperCase().indexOf('MAC') >= 0
          ? '\u2318 + J'
          : 'Ctrl + J',
    },
  );

  return (
    <Layout
      as="header"
      bg="background"
      data-testid={DEPRECATED_REBRAND_PAGE_HEADER_TESTID}
      direction="column"
      styles={styles.sticky.pageHeader}
    >
      <Layout
        align="center"
        bg={isDarkTheme ? 'inverse.background' : 'background'}
        h="header.height.l"
        justify="space-between"
        px={px}
        spacing={px}
        borderBottom={isDarkTheme ? undefined : 'border'}
      >
        <ProductMenu apps={apps} />
        <Link pathname="/">
          <Logo mode="icon" theme={theme} />
        </Link>
        <ContentContainer loadingContent={{ isLoading }}>
          <MainNav
            theme={theme}
            activePathname={activePathname}
            links={links}
          />
        </ContentContainer>

        {ctaButton}

        <Layout preset="icons">
          {preSearchAction}
          <NavPill
            theme={theme}
            icon="search"
            mode="icon"
            text={intl.formatMessage({
              id: 'PageHeader.searchText',
              defaultMessage: 'Search',
              description: 'Text label for the search button',
            })}
            tooltip={searchTooltip}
            onClick={onSearchClick}
          />

          <NavPill
            theme={theme}
            icon="help"
            id="help-button"
            text={intl.formatMessage({
              id: 'PageHeader.helpText',
              defaultMessage: 'Help',
              description: 'Text label for the help button',
            })}
            menu={
              help.actions.length
                ? {
                    name: intl.formatMessage({
                      id: 'PageHeader.helpMenuName',
                      defaultMessage: 'help menu',
                      description: 'Accessible name for the help menu',
                    }),
                    options: help.actions,
                    offsetY: MENU_OFFSET_Y,
                  }
                : undefined
            }
            onClick={onHelpClick}
            mode="icon"
            tooltip={intl.formatMessage({
              id: 'PageHeader.helpTooltip',
              defaultMessage: 'Help',
              description: 'Tooltip text for the help button',
            })}
          />
          <NavPill
            theme={theme}
            isBordered
            mode="image"
            imgSrc={profile.user.avatar}
            text={getUserInitials(profile.user)}
            tooltip={getUserName({
              user: profile.user,
              intl,
            })}
            menu={{
              name: intl.formatMessage({
                id: 'PageHeader.userProfileMenuName',
                defaultMessage: 'user profile menu',
                description: 'Accessible name for the user profile menu',
              }),
              options: profile.settings,
              offsetY: MENU_OFFSET_Y,
            }}
          />
        </Layout>
      </Layout>
      {sublinks && (
        <Box px={px}>
          <SubNav activePathname={activePathname} links={sublinks} />
        </Box>
      )}
      {banner && <StatusMessage {...banner} mode="banner" />}
    </Layout>
  );
};

const px = 8;
/** Offset to Y axis to improve menu visibility in Nav bar */
const MENU_OFFSET_Y = 8;
